import { z } from "zod";

// API path constants
export const API_PATHS = {
  REGISTER: (roomId: string) => `/api/room/${roomId}/register`,
  UNREGISTER: (roomId: string) => `/api/room/${roomId}/unregister`,
  USERS: (roomId: string) => `/api/room/${roomId}/users`,
  WEBSOCKET: (roomId: string) => `/api/room/${roomId}/websocket`,
} as const;

export function roomIdFromPath(path: string): string | undefined {
  const pathParts = path.split("/");
  const roomIndex = pathParts.findIndex((part) => part === "room");
  return roomIndex !== -1 && roomIndex + 1 < pathParts.length
    ? pathParts[roomIndex + 1]
    : undefined;
}

// Request schemas
export const RegisterUserRequestSchema = z.object({
  userId: z.string(),
});

export const UnregisterUserRequestSchema = z.object({
  userId: z.string(),
});

// Response schemas
export const RegisteredUserSchema = z.object({
  userId: z.string(),
  registeredAt: z.number(),
});

export const RegisterUserResponseSchema = z.object({
  success: z.boolean(),
  registeredUsers: z.array(RegisteredUserSchema),
});

export const UnregisterUserResponseSchema = z.object({
  success: z.boolean(),
  registeredUsers: z.array(RegisteredUserSchema),
});

export const GetUsersResponseSchema = z.object({
  users: z.array(RegisteredUserSchema),
});

// Types
export type RegisterUserRequest = z.infer<typeof RegisterUserRequestSchema>;
export type UnregisterUserRequest = z.infer<typeof UnregisterUserRequestSchema>;
export type RegisteredUser = z.infer<typeof RegisteredUserSchema>;
export type RegisterUserResponse = z.infer<typeof RegisterUserResponseSchema>;
export type UnregisterUserResponse = z.infer<
  typeof UnregisterUserResponseSchema
>;
export type GetUsersResponse = z.infer<typeof GetUsersResponseSchema>;

// Client API functions
export async function registerUser(
  roomId: string,
  userId: string,
): Promise<RegisterUserResponse> {
  const response = await fetch(API_PATHS.REGISTER(roomId), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to register user: ${response.statusText}`);
  }

  const data = await response.json();
  return RegisterUserResponseSchema.parse(data);
}

export async function unregisterUser(
  roomId: string,
  userId: string,
): Promise<UnregisterUserResponse> {
  const response = await fetch(API_PATHS.UNREGISTER(roomId), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to unregister user: ${response.statusText}`);
  }

  const data = await response.json();
  return UnregisterUserResponseSchema.parse(data);
}

export async function getRegisteredUsers(
  roomId: string,
): Promise<GetUsersResponse> {
  const response = await fetch(API_PATHS.USERS(roomId), {
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(`Failed to get registered users: ${response.statusText}`);
  }

  const data = await response.json();
  return GetUsersResponseSchema.parse(data);
}
